import React, { useState } from 'react';
import { isPermitted, useGetUserQuery } from 'services/userApi';
import { AgingBucket, BuyerCheckAgingRow } from 'services/reportsApi';
import { BucketCells, VendorPivotRow } from './pivot';
import ReissuedBadge from './ReissuedBadge';

interface Props {
  groups: VendorPivotRow[];
}

const BUCKET_COLUMNS: AgingBucket[] = ['0-30', '31-60', '61-90', '90+'];

const fmtMoney = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
};

const Cell: React.FC<{ cell: BucketCells[AgingBucket] }> = ({ cell }) => {
  if (cell.count === 0) return <span className="text-gray-400">—</span>;
  return (
    <span>
      <span className="font-semibold">{cell.count}</span>
      <span className="text-gray-500"> / {fmtMoney(cell.total_amount)}</span>
    </span>
  );
};

const DetailTable: React.FC<{
  rows: BuyerCheckAgingRow[];
  canViewCheckImage: boolean;
}> = ({ rows, canViewCheckImage }) => {
  const sorted = [...rows].sort((a, b) => b.days_outstanding - a.days_outstanding);
  return (
    <table className="w-full bg-gray-50 text-12s">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="px-4 py-2">Check #</th>
          <th className="px-4 py-2">Property</th>
          <th className="px-4 py-2 text-right">Amount</th>
          <th className="px-4 py-2">Mailed on (earliest)</th>
          <th className="px-4 py-2 text-right">Days outstanding</th>
          <th className="px-4 py-2">Status</th>
          {canViewCheckImage && <th className="px-4 py-2">Image</th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.transfer_id} className="border-t border-solid border-gray-220">
            <td className="px-4 py-2 font-mono">
              {r.current_check_number ?? '—'}
              {r.reissued && <ReissuedBadge count={r.check_count} history={r.check_history} />}
            </td>
            <td className="px-4 py-2">{r.property_name ?? '—'}</td>
            <td className="px-4 py-2 text-right">{fmtMoney(Number(r.amount) || 0)}</td>
            <td className="px-4 py-2">{fmtDate(r.earliest_mailed_on)}</td>
            <td className="px-4 py-2 text-right">{r.days_outstanding}</td>
            <td className="px-4 py-2">{r.current_check_status ?? '—'}</td>
            {canViewCheckImage && (
              <td className="px-4 py-2">
                {r.current_check_image_url ? (
                  <a
                    href={r.current_check_image_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-500 underline"
                  >
                    View
                  </a>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const VendorPivotTable: React.FC<Props> = ({ groups }) => {
  const { data: user } = useGetUserQuery({});
  const canViewCheckImage = Boolean(isPermitted(user, 'view-check-image-url'));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="w-full overflow-x-auto rounded-lg bg-white shadow-analyticsCard">
      <table className="w-full text-14s">
        <thead>
          <tr className="border-b border-solid border-gray-220 text-left text-12s uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Vendor</th>
            {BUCKET_COLUMNS.map((b) => (
              <th key={b} className="px-4 py-3">
                {b} days
              </th>
            ))}
            <th className="px-4 py-3">Total</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const key = g.account_id ?? `__unmapped__-${g.vendor_name}`;
            const isOpen = expanded.has(key);
            return (
              <React.Fragment key={key}>
                <tr
                  className="cursor-pointer border-b border-solid border-gray-220 hover:bg-gray-50"
                  onClick={() => toggle(key)}
                >
                  <td className="px-4 py-3">
                    <span className="mr-2 inline-block w-3 text-gray-500">
                      {isOpen ? '▾' : '▸'}
                    </span>
                    {g.vendor_name}
                  </td>
                  {BUCKET_COLUMNS.map((b) => (
                    <td key={b} className="px-4 py-3">
                      <Cell cell={g.buckets[b]} />
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <Cell cell={g.total} />
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={BUCKET_COLUMNS.length + 2} className="p-0">
                      <DetailTable rows={g.rows} canViewCheckImage={canViewCheckImage} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default VendorPivotTable;
